import { useContext } from 'react';
import {
  addDays,
  format,
  isAfter,
  isWeekend,
  parse,
  startOfDay,
} from 'date-fns';
import useAppDispatch from 'hooks/useAppDispatch';
import { Job } from 'models/Jobs';
import {
  updatePropertiesEntries,
  updatePropertySeasonalRules,
} from 'store/slices/stackedCalendar';
import {
  getActiveSeasonalRulesForDate,
  groupSeasonalCancellationPoliciesByProperty,
} from 'pages/calendar/utils/utils';
import { Lead, LeadStatus } from '../../models/Leads';
import { PropertyBusinessType } from '../../models/Properties';
import {
  getLeadCheckInDate,
  getLeadCheckOutDate,
  isFromICS,
} from '../../utils/lead/leadUtils';
import { getJobStartDate } from '../../utils/job/jobUtils';
import CalendarContext from './CalendarContext';
import {
  CalendarDayCellsData,
  DayCellData,
  PropertiesDayCellDataMap,
} from './Calendar.types';
import { propertiesBusinessType } from './Calendar.constants';

// Display order: BLOCKED leads first, ICS-sourced leads last.
const compareLeadsForDisplay = (a: Lead, b: Lead) => {
  if (a.status === LeadStatus.BLOCKED) return -1;
  if (isFromICS(a.source)) return 1;
  if (isFromICS(b.source)) return -1;
  return 0;
};

// Bucket leads by (propertyOrUnitUid, 'yyyy-MM-dd') across [checkIn, checkOut].
function bucketLeadsByPropertyDay(
  leads: Lead[],
): Map<string, Map<string, Lead[]>> {
  const result = new Map<string, Map<string, Lead[]>>();
  leads.forEach((lead) => {
    const propertyKey = (lead.unitUid || lead.property?.uid) as string;
    if (!propertyKey) return;
    let propertyBucket = result.get(propertyKey);
    if (!propertyBucket) {
      propertyBucket = new Map();
      result.set(propertyKey, propertyBucket);
    }
    // Normalize so the check-out day is always included.
    const checkIn = startOfDay(getLeadCheckInDate(lead));
    const checkOut = startOfDay(getLeadCheckOutDate(lead));
    for (let d = checkIn; !isAfter(d, checkOut); d = addDays(d, 1)) {
      const dayKey = format(d, 'yyyy-MM-dd');
      let dayBucket = propertyBucket.get(dayKey);
      if (!dayBucket) {
        dayBucket = [];
        propertyBucket.set(dayKey, dayBucket);
      }
      dayBucket.push(lead);
    }
  });
  // Sort once per day-bucket (was per-cell).
  result.forEach((propertyBucket) => {
    propertyBucket.forEach((dayBucket) => {
      dayBucket.sort(compareLeadsForDisplay);
    });
  });
  return result;
}

// Jobs bucket the same way but are single-day (start date only).
function bucketJobsByPropertyDay(jobs: Job[]): Map<string, Map<string, Job[]>> {
  const result = new Map<string, Map<string, Job[]>>();
  jobs.forEach((job) => {
    const propertyKey = job.unitUid || job.propertyUid;
    const startDate = getJobStartDate(job);
    if (!startDate) return;
    const dayKey = format(startDate, 'yyyy-MM-dd');
    let propertyBucket = result.get(propertyKey);
    if (!propertyBucket) {
      propertyBucket = new Map();
      result.set(propertyKey, propertyBucket);
    }
    let dayBucket = propertyBucket.get(dayKey);
    if (!dayBucket) {
      dayBucket = [];
      propertyBucket.set(dayKey, dayBucket);
    }
    dayBucket.push(job);
  });
  return result;
}

// Shared sentinel for empty cells — consumers only read, never mutate.
const EMPTY_UIDS: string[] = [];

function getLeadUidsForCell(
  buckets: Map<string, Map<string, Lead[]>>,
  propertyUid: string,
  dayKey: string,
): string[] {
  const dayBucket = buckets.get(propertyUid)?.get(dayKey);
  if (!dayBucket) return EMPTY_UIDS;
  return dayBucket.map((l) => l.uid);
}

function getJobUidsForCell(
  buckets: Map<string, Map<string, Job[]>>,
  propertyUid: string,
  dayKey: string,
): string[] {
  const dayBucket = buckets.get(propertyUid)?.get(dayKey);
  if (!dayBucket) return EMPTY_UIDS;
  return dayBucket.map((j) => j.uid);
}

const useGetCalendarDataProcess = () => {
  const dispatch = useAppDispatch();
  const { jobsMap, leadsMap } = useContext(CalendarContext);

  return ([
    properties,
    leads,
    jobs,
    seasonalCancellationPolicies,
  ]: CalendarDayCellsData) => {
    const datesMap: Record<string, Date> = {};
    const referenceDate = new Date();
    const getDayDate = (dateStr: string) => {
      if (!datesMap[dateStr]) {
        datesMap[dateStr] = parse(dateStr, 'yyyy-MM-dd', referenceDate);
      }
      return datesMap[dateStr];
    };

    const leadsBuckets = bucketLeadsByPropertyDay(leads);
    const jobsBuckets = bucketJobsByPropertyDay(jobs);
    const propertySeasonalRulesMap =
      groupSeasonalCancellationPoliciesByProperty(seasonalCancellationPolicies);

    // Mutate the in-context jobsMap/leadsMap directly
    jobs.forEach((job) => {
      jobsMap[job.uid] = job;
    });
    leads.forEach((lead) => {
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Lead vs CalendarLead — same as the pre-refactor reduce.
      leadsMap[lead.uid] = lead;
    });

    const updatedEntries: PropertiesDayCellDataMap = {};

    // Properties pass (standalone properties + unit-types + sub-units).
    properties.forEach((property) => {
      const { entries, propertyUid } = property;

      const newDayEntries: Record<string, DayCellData> = {};
      entries.forEach((entry) => {
        const dayDate = getDayDate(entry.date);
        const isGrayedOut =
          propertiesBusinessType[propertyUid]?.type ===
          PropertyBusinessType.HOTEL;

        newDayEntries[entry.date] = {
          dayDate,
          entry,
          isEmpty: false,
          isGrayedOut,
          isLoading: false,
          isWeekend: isWeekend(dayDate),
          leads: getLeadUidsForCell(leadsBuckets, propertyUid, entry.date),
          jobs: getJobUidsForCell(jobsBuckets, propertyUid, entry.date),
          activeSeasonalRules: getActiveSeasonalRulesForDate(
            propertySeasonalRulesMap[propertyUid],
            entry.date,
          ),
        };
      });

      const existingDayEntries = updatedEntries[propertyUid];
      if (existingDayEntries) {
        Object.assign(existingDayEntries, newDayEntries);
      } else {
        updatedEntries[propertyUid] = newDayEntries;
      }
    });

    const unitPropertyUids = Object.keys(propertiesBusinessType).filter(
      (key) => propertiesBusinessType[key]?.type === PropertyBusinessType.UNIT,
    );

    unitPropertyUids.forEach((unitPropertyUid) => {
      const unitTypeUid = propertiesBusinessType[unitPropertyUid].parent;
      // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const unitTypeEntries = updatedEntries[unitTypeUid];

      Object.keys(datesMap).forEach((key) => {
        const dayDate = datesMap[key];
        const unitTypeDayEntry = unitTypeEntries?.[key];

        const dayEntry = {
          entry: unitTypeDayEntry?.entry,
          dayDate,
          isEmpty: true,
          isLoading: false,
          leads: getLeadUidsForCell(leadsBuckets, unitPropertyUid, key),
          jobs: getJobUidsForCell(jobsBuckets, unitPropertyUid, key),
          isWeekend: isWeekend(dayDate),
          activeSeasonalRules: getActiveSeasonalRulesForDate(
            unitTypeUid ? propertySeasonalRulesMap[unitTypeUid] : undefined,
            key,
          ),
        };

        const existingDayEntries = updatedEntries[unitPropertyUid];
        if (existingDayEntries) {
          existingDayEntries[key] = dayEntry;
        } else {
          updatedEntries[unitPropertyUid] = { [key]: dayEntry };
        }
      });
    });

    dispatch(updatePropertiesEntries(updatedEntries));
    dispatch(updatePropertySeasonalRules(propertySeasonalRulesMap));
  };
};

export default useGetCalendarDataProcess;
